import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../lib/apiClient.js';

function fmt(val) {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (val === null || val === undefined) return '—';
  return String(val);
}

function AmendmentHistory({ log, loading }) {
  if (loading) return <div className="h-16 animate-pulse bg-bg-s2 rounded" />;
  if (!log.length) return (
    <p className="text-sm text-text-faint text-center py-6">No amendments recorded yet.</p>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="bg-bg-s3 border-b border-border">
            <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">Date</th>
            <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">Changed By</th>
            <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5">Changes</th>
            <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5">Reason</th>
          </tr>
        </thead>
        <tbody>
          {log.map((r) => (
            <tr key={r.id} className="border-b border-border last:border-0 hover:bg-bg-s2 transition-colors align-top">
              <td className="px-4 py-3 text-text-muted whitespace-nowrap">
                {new Date(r.amended_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </td>
              <td className="px-4 py-3 text-text-muted whitespace-nowrap">{r.amended_by}</td>
              <td className="px-4 py-3">
                <div className="space-y-1.5">
                  {r.fields_changed.map(f => (
                    <div key={f} className="flex items-center gap-2 whitespace-nowrap">
                      <span className="font-mono text-[10px] bg-bg-s3 text-text-faint px-1.5 py-0.5 rounded min-w-0">
                        {f.replace(/_/g, ' ')}
                      </span>
                      <span className="text-danger">{fmt(r.old_values[f])}</span>
                      <span className="text-text-faint">→</span>
                      <span className="text-success">{fmt(r.new_values[f])}</span>
                    </div>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 text-text-muted max-w-[200px]">
                <span className="truncate block">{r.reason || <em className="text-text-faint">—</em>}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border bg-bg-s2">
        <h2 className="text-[13px] font-semibold text-text">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-text">{label}</p>
        {description && <p className="text-[11px] text-text-faint mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={[
          'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent',
          'transition-colors duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent/50',
          checked ? 'bg-accent' : 'bg-bg-s3',
        ].join(' ')}
      >
        <span
          className={[
            'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200',
            checked ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

function NumberInput({ label, description, value, onChange, min, max, unit }) {
  return (
    <div>
      <label className="block text-sm font-medium text-text mb-1">{label}</label>
      {description && <p className="text-[11px] text-text-faint mb-1.5">{description}</p>}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-24 rounded-md border border-border bg-bg-s2 px-3 py-1.5 text-sm text-text focus:border-accent focus:ring-1 focus:ring-accent/40 focus:outline-none"
        />
        {unit && <span className="text-sm text-text-faint">{unit}</span>}
      </div>
    </div>
  );
}

export default function Amendments() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);

  // Loan policy state
  const [loansPermitted, setLoansPermitted] = useState(true);
  const [maxOutstandingLoans, setMaxOutstandingLoans] = useState(2);
  const [maxRepaymentGeneral, setMaxRepaymentGeneral] = useState(5);
  const [maxRepaymentPrimaryRes, setMaxRepaymentPrimaryRes] = useState(10);

  // Hardship policy state
  const [hardshipPermitted, setHardshipPermitted] = useState(true);

  // Amendment reason
  const [reason, setReason] = useState('');

  const loadLog = useCallback(() => {
    setLogLoading(true);
    apiClient.getAmendmentLog()
      .then((data) => setLog(data.amendments || []))
      .catch(() => setLog([]))
      .finally(() => setLogLoading(false));
  }, []);

  useEffect(() => {
    apiClient.getPlanProfile()
      .then((data) => {
        setProfile(data);
        const lp = data.loan_policy || {};
        const hp = data.hardship_policy || {};
        setLoansPermitted(lp.loans_permitted ?? true);
        setMaxOutstandingLoans(lp.max_outstanding_loans ?? 2);
        setMaxRepaymentGeneral(lp.max_repayment_years_general ?? 5);
        setMaxRepaymentPrimaryRes(lp.max_repayment_years_primary_res ?? 10);
        setHardshipPermitted(hp.hardship_permitted ?? true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadLog();
  }, [loadLog]);

  async function handleSave() {
    setSaving(true);
    setStatus(null);
    try {
      const res = await apiClient.applyPlanAmendment({
        loan_policy: {
          loans_permitted: loansPermitted,
          max_outstanding_loans: maxOutstandingLoans,
          max_repayment_years_general: maxRepaymentGeneral,
          max_repayment_years_primary_res: maxRepaymentPrimaryRes,
        },
        hardship_policy: { hardship_permitted: hardshipPermitted },
        amendment_reason: reason,
      });
      setStatus({ ok: true, msg: `Amendment applied. Changes: ${res.changes_applied.join(', ')} — effective ${res.effective_date}.` });
      setReason('');
      loadLog();
    } catch (e) {
      setStatus({ ok: false, msg: e.message || 'Amendment failed.' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="card p-5 space-y-3 animate-pulse">
            <div className="h-3 w-32 bg-bg-s3 rounded" />
            <div className="h-8 bg-bg-s3 rounded" />
            <div className="h-8 bg-bg-s3 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[18px] font-bold text-text leading-tight">{profile?.plan_name || 'Plan Amendments'}</h1>
          <p className="text-sm text-text-muted mt-1">
            Amendments are effective immediately. All changes are logged per ERISA § 402(b).
          </p>
        </div>
      </div>

      <div className="card p-3.5 border-l-4 border-warning bg-warning/5">
        <p className="text-[12px] text-warning-dark font-medium">
          Material plan amendments affecting benefits or eligibility require 15-day advance notice to participants
          (ERISA § 204(h)). Consult ERISA counsel before reducing accrued benefits.
        </p>
      </div>

      {/* Loan Policy */}
      <Section title="Loan Policy  —  IRC § 72(p)">
        <Toggle
          label="Loans Permitted"
          description="Allow participants to borrow from their vested balance."
          checked={loansPermitted}
          onChange={setLoansPermitted}
        />
        {loansPermitted && (
          <>
            <div className="border-t border-border pt-4 grid grid-cols-2 gap-4">
              <NumberInput
                label="Max Outstanding Loans"
                description="Maximum number of loans per participant at one time."
                value={maxOutstandingLoans}
                onChange={setMaxOutstandingLoans}
                min={1}
                max={5}
                unit="loans"
              />
              <NumberInput
                label="Max Repayment — General"
                description="Maximum repayment period for general-purpose loans."
                value={maxRepaymentGeneral}
                onChange={setMaxRepaymentGeneral}
                min={1}
                max={5}
                unit="years"
              />
              <NumberInput
                label="Max Repayment — Primary Residence"
                description="Extended repayment period for primary home purchase loans."
                value={maxRepaymentPrimaryRes}
                onChange={setMaxRepaymentPrimaryRes}
                min={5}
                max={30}
                unit="years"
              />
            </div>
            <p className="text-[11px] text-text-faint">
              IRC § 72(p) cap: max loan is lesser of $50,000 or 50% of vested balance, regardless of plan policy.
            </p>
          </>
        )}
      </Section>

      {/* Hardship Policy */}
      <Section title="Hardship Distributions  —  IRC § 401(k)(2)(B)(i)(IV)">
        <Toggle
          label="Hardship Distributions Permitted"
          description="Allow participants to request distributions for immediate and heavy financial need."
          checked={hardshipPermitted}
          onChange={setHardshipPermitted}
        />
        {hardshipPermitted && (
          <div className="border-t border-border pt-3">
            <p className="text-[12px] text-text-faint">
              Plan uses IRS safe harbor standard (Treas. Reg. § 1.401(k)-1(d)(3)). Qualifying expenses:
              medical, tuition, primary home purchase, eviction prevention, funeral, casualty loss, FEMA disaster.
            </p>
          </div>
        )}
      </Section>

      {/* Amendment Reason */}
      <div className="card p-5 space-y-3">
        <label className="block text-sm font-medium text-text">Amendment Reason <span className="text-text-faint font-normal">(optional)</span></label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="E.g. Annual plan review — adjusted loan limit per committee resolution dated 2026-01-15"
          className="w-full rounded-md border border-border bg-bg-s2 px-3 py-2 text-sm text-text placeholder:text-text-faint focus:border-accent focus:ring-1 focus:ring-accent/40 focus:outline-none resize-none"
        />
        {status && (
          <div className={`rounded-md px-3 py-2 text-[12px] ${status.ok ? 'bg-success/10 text-success border border-success/30' : 'bg-danger/10 text-danger border border-danger/30'}`}>
            {status.msg}
          </div>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-white hover:bg-accent-dark transition-colors disabled:opacity-50"
        >
          {saving ? 'Applying Amendment…' : 'Apply Amendment'}
        </button>
      </div>

      {/* Amendment History */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border bg-bg-s2 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-text">Amendment History</h2>
          <span className="text-[10px] font-mono text-text-faint">ERISA § 402(b) · 6-yr retention required</span>
        </div>
        <AmendmentHistory log={log} loading={logLoading} />
      </div>

      <p className="text-[11px] text-text-faint text-center font-mono pb-2">
        ERISA § 402(b) requires amendments to be in writing. Keep adopted amendment documents on file.
      </p>
    </div>
  );
}
